import { router } from "@inertiajs/react";
import { Autocomplete, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { CompaniesTable } from "../../components/company/CompaniesTable";
import { FilterSection } from "../../components/filter/FilterSection";
import { showDeleteModal } from "../../helpers/modal";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { Company } from "../../models/company";
import type { Pagination } from "../../models/pagination";
import { companies_path, company_path, edit_company_path, new_company_path } from "../../routes";

type CompaniesProps = {
  companies: Company[];
  company_names: string[];
  filter: FilterFormType & {
    sort_key?: "name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  name?: string;
};

const resetFilters: FilterFormType = { name: undefined };

export default function Companies({ companies, filter, company_names, pagination }: CompaniesProps) {
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const { sort_key, sort_direction, ...filterFormData } = filter;

  const sortStatus: DataTableSortStatus<Company> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const filterForm = useForm({
    mode: "uncontrolled",
    initialValues: filterFormData,
  });

  const handlePageUpdate = (filters: FilterFormType, sortStatus: DataTableSortStatus<Company>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(companies_path), {
      only: ["filter", "companies", "pagination"],
      data: {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };
  const sortChange = (sortStatus: DataTableSortStatus<Company>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const filterChange = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const onEdit = (company: Company) => router.get(pathWithYearContext(edit_company_path, company.id));
  const onDelete = (company: Company) =>
    showDeleteModal({
      article: "die",
      title: `Lehrbetrieb ${company.name}`,
      onConfirm: () => router.delete(pathWithYearContext(company_path, company.id)),
    });

  return (
    <ContentShell
      title="Lehrbetriebe"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={() => router.get(pathWithYearContext(new_company_path))}>
          Neu
        </Button>
      }
    >
      <FilterSection
        form={filterForm}
        onSubmit={filterChange}
        onReset={onReset}
        filters={[<Autocomplete data={company_names} label="Name" key={filterForm.key("name")} {...filterForm.getInputProps("name")} />]}
      />
      <CompaniesTable
        companies={companies}
        sortStatus={sortStatus}
        onSortStatusChange={sortChange}
        onEdit={onEdit}
        onDelete={onDelete}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
